// Razorpay Checkout (Standard) popup wrapper.
//
// Single source of truth for opening the Razorpay popup. Lazy-loads
// the official checkout.js script exactly once across the whole app
// session and returns a typed openCheckout() the caller can await.
//
// Usage:
//   const { openCheckout } = useRazorpayCheckout();
//   const result = await openCheckout({
//     orderResp,           // the response from /payments/razorpay/create-order
//     planName,            // string shown in the popup title
//     prefill: { name, email, contact },
//     theme: "#0b1d40",    // optional brand colour
//   });
//   // result is one of:
//   //   { kind: "success", razorpay_order_id, razorpay_payment_id, razorpay_signature }
//   //   { kind: "dismissed" }
//   //   { kind: "failed", code, description, source }

import { useCallback } from "react";

const SCRIPT_SRC = "https://checkout.razorpay.com/v1/checkout.js";

let scriptPromise = null;

function loadRazorpayScript() {
  if (typeof window === "undefined") {
    return Promise.reject(new Error("Razorpay Checkout requires a browser environment"));
  }
  if (window.Razorpay) {
    return Promise.resolve();
  }
  if (scriptPromise) {
    return scriptPromise;
  }
  scriptPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${SCRIPT_SRC}"]`);
    if (existing) {
      existing.addEventListener("load", () => resolve());
      existing.addEventListener("error", () =>
        reject(new Error("Failed to load Razorpay Checkout script"))
      );
      return;
    }
    const tag = document.createElement("script");
    tag.src = SCRIPT_SRC;
    tag.async = true;
    tag.onload = () => resolve();
    tag.onerror = () => {
      scriptPromise = null; // allow retry on next click
      reject(new Error("Failed to load Razorpay Checkout script"));
    };
    document.body.appendChild(tag);
  });
  return scriptPromise;
}

export function useRazorpayCheckout() {
  const openCheckout = useCallback(async ({
    orderResp,
    planName,
    prefill,
    theme,
    description,
  } = {}) => {
    if (!orderResp || !orderResp.razorpay_order_id || !orderResp.key_id) {
      throw new Error("openCheckout: orderResp must include razorpay_order_id + key_id");
    }
    await loadRazorpayScript();

    return new Promise((resolve) => {
      const options = {
        key: orderResp.key_id,
        amount: orderResp.amount_paise,
        currency: orderResp.currency || "INR",
        order_id: orderResp.razorpay_order_id,
        name: "NoticeAI",
        description: description || planName || "Plan purchase",
        prefill: prefill || {},
        theme: { color: theme || "#0b1d40" },
        handler: (rzp) => {
          resolve({
            kind: "success",
            razorpay_order_id: rzp.razorpay_order_id,
            razorpay_payment_id: rzp.razorpay_payment_id,
            razorpay_signature: rzp.razorpay_signature,
          });
        },
        modal: {
          ondismiss: () => resolve({ kind: "dismissed" }),
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", (event) => {
        const err = event?.error || {};
        resolve({
          kind: "failed",
          code: err.code,
          description: err.description,
          source: err.source,
        });
      });
      rzp.open();
    });
  }, []);

  return { openCheckout };
}

export default useRazorpayCheckout;
